import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/admin/launch-kits")({
  head: () => ({
    meta: [
      { title: `Launch kits — ${SITE.name}` },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: LaunchKitsPage,
});

/* ---------------- Data ---------------- */

type Tool = {
  id: string;
  name: string;
  url: string;
  tagline: string; // <=60 chars
  oneLiner: string; // <=140 chars
  description: string; // long description
  categories: string[];
  keywords: string[];
};

const TOOLS: Tool[] = [
  {
    id: "mapobserver",
    name: "Map Observer",
    url: "https://mapobserver.com",
    tagline: "The daily newsroom for local SEO & Google Maps",
    oneLiner:
      "The authoritative daily briefing on Google Business Profile, the Map Pack, and local search — for agencies and multi-location operators.",
    description:
      "Map Observer is an independent newsroom covering Google Business Profile, the Map Pack, local SEO, and AI-driven local search. We publish daily analysis, a public local-search volatility tracker, an evolving GBP category and attribute database, and free tools used by local SEO agencies and in-house operators. Every article is byline-attributed, corrections-tracked, and cited by working practitioners.",
    categories: ["SEO", "Marketing", "News", "Search"],
    keywords: ["local SEO", "google business profile", "map pack", "GBP", "google maps"],
  },
  {
    id: "gbp-categories",
    name: "GBP Category Finder",
    url: "https://mapobserver.com/tools/gbp-categories",
    tagline: "Find the right Google Business Profile category",
    oneLiner:
      "Free searchable database of every Google Business Profile category, with definitions, synonyms, and picker guidance.",
    description:
      "The GBP Category Finder is a free searchable index of every Google Business Profile primary and secondary category. Search by keyword or by what your business actually does; each category page includes plain-English definitions, common synonyms, and notes on when to pick it vs. an adjacent category. Built and maintained by the Map Observer newsroom for local SEO agencies and business owners setting up or auditing a Google Business Profile.",
    categories: ["SEO", "Marketing", "Small Business Tools"],
    keywords: ["GBP categories", "google business profile categories", "local SEO tools"],
  },
  {
    id: "gbp-attributes",
    name: "GBP Attributes Explorer",
    url: "https://mapobserver.com/tools/gbp-attributes",
    tagline: "Every Google Business Profile attribute, explained",
    oneLiner:
      "Free reference of Google Business Profile attributes — accessibility, amenities, service options — with eligibility and impact notes.",
    description:
      "A free, searchable reference to every attribute available on a Google Business Profile — accessibility, amenities, service options, from-the-business, health-and-safety, planning, and payments. Each attribute page explains what the attribute means, which categories can use it, and how it can influence ranking and click-through. Maintained by the Map Observer newsroom.",
    categories: ["SEO", "Marketing", "Small Business Tools"],
    keywords: ["GBP attributes", "google business profile", "local SEO"],
  },
  {
    id: "justifications",
    name: "Justifications Library",
    url: "https://mapobserver.com/tools/justifications",
    tagline: "Every Google Maps justification, cataloged",
    oneLiner:
      "The complete library of Google Maps justifications — review, website, menu, in-stock — with examples and how to trigger them.",
    description:
      "Justifications are the snippet Google surfaces under a Map Pack result that explains why it's ranked (review mention, matching menu item, in-stock product, website content). This free library catalogs every known justification type with real examples and guidance on how to legitimately earn them.",
    categories: ["SEO", "Marketing"],
    keywords: ["google maps justifications", "local SEO", "map pack"],
  },
  {
    id: "suspensions",
    name: "GBP Suspensions Guide",
    url: "https://mapobserver.com/tools/suspensions",
    tagline: "The complete Google Business Profile suspension playbook",
    oneLiner:
      "Every known reason a Google Business Profile gets suspended, plus the recovery steps that actually work in 2026.",
    description:
      "A living reference to every documented Google Business Profile suspension type — soft, hard, and grey-market — with the recovery playbook for each. Sourced from confirmed practitioner cases and updated as Google's policies evolve.",
    categories: ["SEO", "Marketing", "Small Business Tools"],
    keywords: ["GBP suspension", "google business profile suspended", "local SEO"],
  },
  {
    id: "local-schema",
    name: "Local Business Schema Generator",
    url: "https://mapobserver.com/tools/local-schema",
    tagline: "Free structured-data generator for local businesses",
    oneLiner:
      "Paste your business details, get valid LocalBusiness JSON-LD schema ready to drop into your site.",
    description:
      "A free structured-data generator that outputs valid Schema.org LocalBusiness JSON-LD tailored to your business type (restaurant, dentist, plumber, etc.), hours, address, service area, and social profiles. No sign-up, no upsell. Copy-paste output.",
    categories: ["SEO", "Developer Tools", "Marketing"],
    keywords: ["schema generator", "local business schema", "JSON-LD", "structured data"],
  },
  {
    id: "volatility",
    name: "Local Search Volatility Tracker",
    url: "https://mapobserver.com/embed/volatility",
    tagline: "Live volatility index for the Google Map Pack",
    oneLiner:
      "Real-time volatility index tracking Google Map Pack ranking movement across industries and regions.",
    description:
      "An embeddable, free volatility tracker that measures how much the Google Map Pack is shifting day-to-day across industries and regions. Useful for local SEO agencies to correlate client ranking changes with broader Google updates, and to catch algorithm rolls in real time.",
    categories: ["SEO", "Analytics", "Marketing"],
    keywords: ["local SEO", "google update tracker", "SERP volatility", "map pack"],
  },
];

/* ---------------- Platforms ---------------- */

type Platform = {
  id: string;
  name: string;
  submitUrl: string;
  notes: string;
  buildFields: (t: Tool) => { label: string; value: string; long?: boolean }[];
};

const PLATFORMS: Platform[] = [
  {
    id: "producthunt",
    name: "Product Hunt",
    submitUrl: "https://www.producthunt.com/posts/new",
    notes:
      "Submit at 12:01am PT on a Tue/Wed/Thu for max exposure. Line up 5-10 hunters in advance. First comment (maker comment) matters most — it's what shows above the fold.",
    buildFields: (t) => [
      { label: "Name", value: t.name },
      { label: "Tagline (60 char max)", value: t.tagline },
      { label: "Description", value: t.description, long: true },
      { label: "Topics", value: t.categories.join(", ") },
      {
        label: "Maker comment (first comment)",
        long: true,
        value: `Hey Product Hunt 👋

I'm the maker of ${t.name}. ${t.oneLiner}

Why we built it: Local SEO tooling is dominated by paid, gated products. ${t.name} is free, has no sign-up wall, and is maintained by the Map Observer newsroom — the same team publishing daily coverage of Google Business Profile and the Map Pack.

Happy to answer any questions about local SEO, Google Business Profile, or how we built this. Feedback very welcome.`,
      },
    ],
  },
  {
    id: "betalist",
    name: "BetaList",
    submitUrl: "https://betalist.com/submit",
    notes:
      "Free tier is slow (weeks). Paid ($129+) publishes within days. Good for early traffic and email sign-ups.",
    buildFields: (t) => [
      { label: "Startup name", value: t.name },
      { label: "URL", value: t.url },
      { label: "Pitch (one sentence)", value: t.oneLiner },
      { label: "Description", value: t.description, long: true },
      { label: "Categories", value: t.categories.join(", ") },
    ],
  },
  {
    id: "uneed",
    name: "Uneed",
    submitUrl: "https://www.uneed.best/submit-a-tool",
    notes: "Weekly digest to ~40k subscribers. Free basic listing; paid featured slot.",
    buildFields: (t) => [
      { label: "Tool name", value: t.name },
      { label: "URL", value: t.url },
      { label: "Tagline", value: t.tagline },
      { label: "Description", value: t.description, long: true },
      { label: "Category", value: t.categories[0] ?? "Marketing" },
    ],
  },
  {
    id: "fazier",
    name: "Fazier",
    submitUrl: "https://fazier.com/submit",
    notes: "Product Hunt alternative with weekly winners. Free listing.",
    buildFields: (t) => [
      { label: "Name", value: t.name },
      { label: "Tagline", value: t.tagline },
      { label: "URL", value: t.url },
      { label: "Description", value: t.description, long: true },
      { label: "Tags", value: t.keywords.join(", ") },
    ],
  },
  {
    id: "tinylaunch",
    name: "Tiny Launch",
    submitUrl: "https://tinylaunch.com/",
    notes: "Small but active indie-hacker audience. Free.",
    buildFields: (t) => [
      { label: "Name", value: t.name },
      { label: "Tagline", value: t.tagline },
      { label: "URL", value: t.url },
      { label: "Description", value: t.description, long: true },
    ],
  },
  {
    id: "saashub",
    name: "SaaSHub",
    submitUrl: "https://www.saashub.com/submit-software",
    notes: "SEO-friendly directory. Free listing; verify email to activate.",
    buildFields: (t) => [
      { label: "Software name", value: t.name },
      { label: "Website", value: t.url },
      { label: "Short description", value: t.oneLiner },
      { label: "Full description", value: t.description, long: true },
      { label: "Categories", value: t.categories.join(", ") },
      { label: "Tags", value: t.keywords.join(", ") },
    ],
  },
  {
    id: "alternativeto",
    name: "AlternativeTo",
    submitUrl: "https://alternativeto.net/software/new/",
    notes:
      "Very SEO-strong. Add as an 'Alternative to' Whitespark, BrightLocal, or LocalFalcon depending on the tool.",
    buildFields: (t) => [
      { label: "App name", value: t.name },
      { label: "Website", value: t.url },
      { label: "Tagline", value: t.tagline },
      { label: "Description", value: t.description, long: true },
      { label: "Categories", value: t.categories.join(", ") },
      { label: "Suggested 'Alternative to'", value: "Whitespark, BrightLocal, LocalFalcon, Moz Local" },
    ],
  },
  {
    id: "toolfinder",
    name: "ToolFinder",
    submitUrl: "https://toolfinder.co/submit",
    notes: "Curated productivity/marketing tools directory.",
    buildFields: (t) => [
      { label: "Name", value: t.name },
      { label: "Tagline", value: t.tagline },
      { label: "URL", value: t.url },
      { label: "Description", value: t.description, long: true },
      { label: "Tags", value: t.keywords.join(", ") },
    ],
  },
  {
    id: "theresanaifor",
    name: "There's An AI For That",
    submitUrl: "https://theresanaiforthat.com/submit/",
    notes:
      "Only submit the Local Schema Generator and Volatility Tracker here (they qualify as AI-adjacent). Skip for pure directories.",
    buildFields: (t) => [
      { label: "Name", value: t.name },
      { label: "URL", value: t.url },
      { label: "Tagline", value: t.tagline },
      { label: "Description", value: t.description, long: true },
      { label: "Use cases", value: t.keywords.join(", ") },
    ],
  },
  {
    id: "detailed",
    name: "Detailed.com",
    submitUrl: "https://detailed.com/submit/",
    notes:
      "Glen Allsopp's curated index of the best marketing/SEO sites. High authority. Submit Map Observer (the newsroom) rather than individual tools.",
    buildFields: (t) => [
      { label: "Site name", value: t.name },
      { label: "URL", value: t.url },
      { label: "Why it's notable", value: t.description, long: true },
    ],
  },
  {
    id: "feedspot",
    name: "Feedspot Top Local SEO Blogs",
    submitUrl: "https://blog.feedspot.com/local_seo_blogs/",
    notes:
      "Scroll to the bottom of the list; 'Submit Your Blog' link. Free but slow — expect 2-6 weeks. Great backlink if accepted.",
    buildFields: (t) => [
      { label: "Blog name", value: t.name },
      { label: "URL", value: t.url },
      { label: "Description", value: t.description, long: true },
      { label: "RSS feed", value: "https://mapobserver.com/rss.xml" },
    ],
  },
  {
    id: "awesome-seo",
    name: "awesome-seo (GitHub PR)",
    submitUrl: "https://github.com/marcobiedermann/search-engine-optimization",
    notes:
      "Fork the repo, add Map Observer under 'Blogs' or 'News' section in README.md, open PR. Also try `awesome-local-seo` if it exists.",
    buildFields: (t) => [
      {
        label: "Markdown line to add",
        long: true,
        value: `- [${t.name}](${t.url}) — ${t.oneLiner}`,
      },
      { label: "PR title", value: `Add ${t.name}` },
      {
        label: "PR body",
        long: true,
        value: `Adding [${t.name}](${t.url}) — ${t.oneLiner}\n\nIndependent daily newsroom covering Google Business Profile, the Map Pack, and local search. Publishes original analysis, a public volatility tracker, and free reference tools. Not a rehost — original reporting with bylines and corrections tracking.`,
      },
    ],
  },
];

/* ---------------- UI ---------------- */

function LaunchKitsPage() {
  const [toolId, setToolId] = useState<string>(TOOLS[0].id);
  const [platformId, setPlatformId] = useState<string>(PLATFORMS[0].id);
  const tool = useMemo(() => TOOLS.find((t) => t.id === toolId)!, [toolId]);
  const platform = useMemo(() => PLATFORMS.find((p) => p.id === platformId)!, [platformId]);
  const fields = useMemo(() => platform.buildFields(tool), [platform, tool]);
  const [copied, setCopied] = useState<string | null>(null);

  function copy(label: string, value: string) {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 1200);
    });
  }

  return (
    <div className="grid grid-cols-[220px_1fr] gap-8">
      <aside>
        <h2 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Products</h2>
        <nav className="flex flex-col">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => setToolId(t.id)}
              className={
                "text-left px-3 py-2 text-sm border-l-2 transition-colors " +
                (t.id === toolId
                  ? "border-foreground text-foreground bg-paper-soft"
                  : "border-transparent text-muted-foreground hover:text-foreground")
              }
            >
              {t.name}
            </button>
          ))}
        </nav>
      </aside>

      <section>
        <div className="mb-6">
          <h1 className="headline-display text-3xl">{tool.name}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            <a href={tool.url} target="_blank" rel="noreferrer" className="underline">
              {tool.url}
            </a>
          </p>
        </div>

        <div className="flex flex-wrap gap-1 mb-6 border-b border-border">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPlatformId(p.id)}
              className={
                "px-3 py-1.5 text-sm border-b-2 transition-colors -mb-px " +
                (p.id === platformId
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground")
              }
            >
              {p.name}
            </button>
          ))}
        </div>

        <div className="mb-4 flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-xl">{platform.notes}</p>
          <a
            href={platform.submitUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded border border-foreground px-3 py-1.5 text-sm hover:bg-foreground hover:text-background transition-colors"
          >
            Open submit page ↗
          </a>
        </div>

        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.label} className="border border-border rounded p-3 bg-paper-soft">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  {f.label}
                </label>
                <button
                  onClick={() => copy(f.label, f.value)}
                  className="text-xs underline text-muted-foreground hover:text-foreground"
                >
                  {copied === f.label ? "Copied ✓" : "Copy"}
                </button>
              </div>
              {f.long ? (
                <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">
                  {f.value}
                </pre>
              ) : (
                <div className="text-sm">{f.value}</div>
              )}
              {!f.long && (
                <div className="text-xs text-muted-foreground mt-1">
                  {f.value.length} chars
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
